/**
 * PermissionGate Component
 * 
 * Conditionally renders children based on user permissions.
 * Updated for workspace-based RBAC system.
 */

import { ReactNode } from 'react';
import { Permission, WorkspaceRole, WORKSPACE_ROLE_DISPLAY_NAMES } from '../types/rbac';
import { useRBAC, useWorkspaceRole } from '../hooks/useRBAC';

// ==================== PERMISSION GATE ====================

interface PermissionGateProps {
  /** Single permission required */
  permission?: Permission;
  /** Multiple permissions - any one is sufficient */
  anyOf?: Permission[];
  /** Multiple permissions - all are required */
  allOf?: Permission[];
  /** Minimum workspace role required */
  minRole?: WorkspaceRole;
  /** Content to show when permission is granted */
  children: ReactNode;
  /** Content to show when permission is denied (optional) */
  fallback?: ReactNode;
  /** If true, shows loading state while permissions are loading */
  showLoading?: boolean;
}

export function PermissionGate({
  permission,
  anyOf,
  allOf,
  minRole,
  children,
  fallback = null,
  showLoading = false,
}: PermissionGateProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isRoleOrHigher, loading } = useRBAC();

  if (loading && showLoading) {
    return <span className="permission-loading">Loading...</span>;
  }

  if (loading) {
    return null;
  }

  let hasAccess = true;

  // Check single permission
  if (permission) {
    hasAccess = hasAccess && hasPermission(permission);
  }

  // Check any of permissions
  if (anyOf && anyOf.length > 0) {
    hasAccess = hasAccess && hasAnyPermission(anyOf);
  }

  // Check all of permissions
  if (allOf && allOf.length > 0) {
    hasAccess = hasAccess && hasAllPermissions(allOf);
  }

  // Check minimum role
  if (minRole) {
    hasAccess = hasAccess && isRoleOrHigher(minRole);
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// ==================== ROLE GATE ====================

interface RoleGateProps {
  /** Minimum workspace role required */
  role: WorkspaceRole;
  /** Content to show when role requirement is met */
  children: ReactNode;
  /** Content to show when role requirement is not met */
  fallback?: ReactNode;
}

export function RoleGate({ role, children, fallback = null }: RoleGateProps) {
  const hasRole = useWorkspaceRole(role);

  if (hasRole) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// ==================== OWNER ONLY ====================

interface OwnerOnlyProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function OwnerOnly({ children, fallback = null }: OwnerOnlyProps) {
  return (
    <RoleGate role="OWNER" fallback={fallback}>
      {children}
    </RoleGate>
  );
}

// ==================== ORG ADMIN OR HIGHER ====================

interface OrgAdminOrHigherProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function OrgAdminOrHigher({ children, fallback = null }: OrgAdminOrHigherProps) {
  return (
    <RoleGate role="ORG_ADMIN" fallback={fallback}>
      {children}
    </RoleGate>
  );
}

// ==================== MANAGER OR HIGHER ====================

interface ManagerOrHigherProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function ManagerOrHigher({ children, fallback = null }: ManagerOrHigherProps) {
  return (
    <RoleGate role="MANAGER" fallback={fallback}>
      {children}
    </RoleGate>
  );
}

// ==================== CAN EDIT GATE ====================

interface CanEditGateProps {
  /** Owner ID of the item */
  itemOwnerId?: string;
  children: ReactNode;
  fallback?: ReactNode;
}

export function CanEditGate({ itemOwnerId, children, fallback = null }: CanEditGateProps) {
  const { canEdit } = useRBAC();

  if (canEdit(itemOwnerId)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// ==================== CAN DELETE GATE ====================

interface CanDeleteGateProps {
  /** Owner ID of the item */
  itemOwnerId?: string;
  children: ReactNode;
  fallback?: ReactNode;
}

export function CanDeleteGate({ itemOwnerId, children, fallback = null }: CanDeleteGateProps) {
  const { canDelete } = useRBAC();

  if (canDelete(itemOwnerId)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// ==================== NOT VIEWER GATE ====================

interface NotViewerProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function NotViewer({ children, fallback = null }: NotViewerProps) {
  return (
    <RoleGate role="REVIEWER" fallback={fallback}>
      {children}
    </RoleGate>
  );
}

// ==================== REVIEWER MODE INDICATOR ====================

interface ReviewerModeProps {
  children: ReactNode;
}

export function ReviewerModeWrapper({ children }: ReviewerModeProps) {
  const { workspaceRole } = useRBAC();

  if (workspaceRole === 'REVIEWER') {
    return (
      <div className="reviewer-mode">
        <div className="reviewer-banner">
          <span className="reviewer-icon">📝</span>
          <span>Review Mode - Your edits will be submitted as suggestions</span>
        </div>
        {children}
      </div>
    );
  }

  return <>{children}</>;
}

// ==================== VIEWER BADGE ====================

export function ViewOnlyBadge() {
  const { workspaceRole } = useRBAC();

  if (workspaceRole === 'VIEWER' || workspaceRole === 'GUEST') {
    return (
      <span className="view-only-badge">
        View Only
      </span>
    );
  }

  return null;
}

// ==================== ROLE BADGE ====================

interface RoleBadgeProps {
  role?: WorkspaceRole;
  size?: 'sm' | 'md' | 'lg';
}

const ROLE_COLORS: Record<WorkspaceRole, string> = {
  OWNER: '#ff4444',
  ORG_ADMIN: '#ff8800',
  MANAGER: '#44bb44',
  REVIEWER: '#aa44ff',
  VIEWER: '#888888',
  GUEST: '#cccccc',
};

export function RoleBadge({ role, size = 'md' }: RoleBadgeProps) {
  const { workspaceRole } = useRBAC();
  const displayRole = role || workspaceRole;

  if (!displayRole) {
    return null;
  }

  const sizeClasses = {
    sm: 'role-badge-sm',
    md: 'role-badge-md',
    lg: 'role-badge-lg',
  };

  return (
    <span 
      className={`role-badge ${sizeClasses[size]}`}
      style={{ backgroundColor: ROLE_COLORS[displayRole] }}
    >
      {WORKSPACE_ROLE_DISPLAY_NAMES[displayRole]}
    </span>
  );
}

// ==================== PLATFORM ADMIN GATE ====================

interface PlatformAdminOnlyProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function PlatformAdminOnly({ children, fallback = null }: PlatformAdminOnlyProps) {
  const { isPlatformAdmin, loading } = useRBAC();

  if (loading) return null;

  if (isPlatformAdmin) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// ==================== WORKSPACE REQUIRED GATE ====================

interface WorkspaceRequiredProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function WorkspaceRequired({ children, fallback = null }: WorkspaceRequiredProps) {
  const { currentWorkspace, loading } = useRBAC();

  if (loading) return null;

  if (currentWorkspace) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// ==================== LEGACY ALIASES ====================

/** @deprecated Use OwnerOnly instead */
export const SuperAdminOnly = OwnerOnly;

/** @deprecated Use OrgAdminOrHigher instead */
export const AdminOrHigher = OrgAdminOrHigher;

// ==================== EXPORTS ====================

const PermissionGateExports = {
  PermissionGate,
  RoleGate,
  OwnerOnly,
  OrgAdminOrHigher,
  ManagerOrHigher,
  CanEditGate,
  CanDeleteGate,
  NotViewer,
  ReviewerModeWrapper,
  ViewOnlyBadge,
  RoleBadge,
  PlatformAdminOnly,
  WorkspaceRequired,
  // Legacy
  SuperAdminOnly,
  AdminOrHigher,
};

export default PermissionGateExports;
